import { c as _c } from "react/compiler-runtime";
import { getFieldErrorsFromContext } from '@onefootprint/core';
import { getHostedBusinessOwnersOptions, patchHostedBusinessOwnersMutation, patchHostedUserVaultMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/fetch-hosted/use-request-error';
import type { HostedBusinessOwner, VaultData } from '@onefootprint/request-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { useNavigationOverride } from '@/idv2/features/app-shell/hooks/use-navigation-override';
import { NavigationHeader } from '@/idv2/features/app-shell/navigation-header';
import { setExternalBack, setIdv2StoreData } from '@/idv2/features/idv-to-idv2';
import { type IDV2Store, StoreContext } from '@/idv2/features/idv-to-idv2/idv2-provider';
import { ManageBusinessOwners } from '@/idv2/features/manage-business-owners';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { formatFieldErrorsForLog } from '@/idv2/hooks/use-vault-mutation-error';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'business-owners'
});
export const Route = createFileRoute('/onboarding/$token/collect-business-data/business-owners')({
  component: BusinessOwnersPage,
  loader: ({
    context: {
      currentRequirement,
      businessOwners
    }
  }) => ({
    currentRequirement,
    businessOwners
  })
});
function BusinessOwnersPage() {
  const $ = _c(31);
  const {
    currentRequirement,
    businessOwners: initialBusinessOwners
  } = Route.useLoaderData();
  const {
    token
  } = Route.useParams();
  const footprint = useFootprint();
  const queryClient = useQueryClient();
  const {
    setNavigationOverridden
  } = useNavigationOverride();
  const {
    getErrorContext
  } = useRequestError();
  const store = useContext(StoreContext);
  const hideNavigationButtons = useStore(store, _temp);
  let t0;
  if ($[0] !== footprint.navigation) {
    t0 = () => {
      logInfo("Navigating back to business-information");
      footprint.navigation.forward();
    };
    $[0] = footprint.navigation;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const onBack = t0;
  let t1;
  let t2;
  if ($[2] !== onBack || $[3] !== setNavigationOverridden) {
    t1 = () => {
      setNavigationOverridden(true);
      setExternalBack(onBack);
      setIdv2StoreData({
        canGoBack: true
      });
      return () => {
        setNavigationOverridden(false);
        setExternalBack(undefined);
        setIdv2StoreData({
          canGoBack: false
        });
      };
    };
    t2 = [setNavigationOverridden, onBack];
    $[2] = onBack;
    $[3] = setNavigationOverridden;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(_temp2, t3);
  const headers = {
    "X-Fp-Authorization": token
  };
  const [businessOwners, setBusinessOwners] = useState(initialBusinessOwners);
  const {
    mutateAsync: bosMutation,
    isPending: isBosPending
  } = useMutation(patchHostedBusinessOwnersMutation({
    headers
  }));
  const {
    mutateAsync: userVaultMutation,
    isPending: isUserVaultPending
  } = useMutation(patchHostedUserVaultMutation({
    headers
  }));
  const isPending = isBosPending || isUserVaultPending;
  let t4;
  if ($[7] !== businessOwners || $[8] !== getErrorContext || $[9] !== userVaultMutation) {
    t4 = async operations => {
      const authedUserOp = operations.find(op => {
        const existingBo = businessOwners.find(bo => bo.uuid === op.uuid);
        return existingBo?.isAuthedUser;
      });
      if (!authedUserOp) {
        return;
      }
      const existingBo_0 = businessOwners.find(bo_0 => bo_0.uuid === authedUserOp.uuid);
      const firstName = authedUserOp.data["id.first_name"];
      const lastName = authedUserOp.data["id.last_name"];
      const hasFirstNameChanged = firstName && existingBo_0?.decryptedData["id.first_name"] !== firstName;
      const hasLastNameChanged = lastName && existingBo_0?.decryptedData["id.last_name"] !== lastName;
      if (!hasFirstNameChanged && !hasLastNameChanged) {
        return;
      }
      const userVaultData = {};
      if (hasFirstNameChanged && firstName) {
        userVaultData["id.first_name"] = firstName;
      }
      if (hasLastNameChanged && lastName) {
        userVaultData["id.last_name"] = lastName;
      }
      logInfo("Syncing authed user vault with updated name", {
        uuid: authedUserOp.uuid,
        fieldsUpdated: Object.keys(userVaultData).join(",")
      });
      ;
      try {
        await userVaultMutation({
          body: userVaultData
        });
        logInfo("Authed user vault sync succeeded");
      } catch (t5) {
        const error = t5;
        const fieldErrors = getFieldErrorsFromContext(getErrorContext(error));
        logWarn("Failed to sync authed user vault, continuing with BO update", error, {
          fieldErrors: fieldErrors.length > 0 ? formatFieldErrorsForLog(fieldErrors) : undefined
        });
      }
    };
    $[7] = businessOwners;
    $[8] = getErrorContext;
    $[9] = userVaultMutation;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const syncAuthedUserVault = t4;
  let t5;
  if ($[11] !== bosMutation || $[12] !== businessOwners || $[13] !== syncAuthedUserVault) {
    t5 = async operations_0 => {
      logInfo("Business owners form submission started", {
        createOrUpdateCount: operations_0.updateOrCreateOperations.length,
        deleteCount: operations_0.deleteOperations.length
      });
      await syncAuthedUserVault(operations_0.updateOrCreateOperations);
      const bulkPayload = [...operations_0.updateOrCreateOperations.map(op_0 => {
        const existingBo_1 = businessOwners.find(bo_1 => bo_1.uuid === op_0.uuid);
        if (existingBo_1) {
          return {
            op: "update" as const,
            uuid: op_0.uuid,
            data: op_0.data,
            ownershipStake: op_0.ownershipStake
          };
        }
        return {
          op: "create" as const,
          uuid: op_0.uuid,
          data: op_0.data,
          ownershipStake: op_0.ownershipStake
        };
      }), ...operations_0.deleteOperations.map(_temp3)];
      await bosMutation({
        body: bulkPayload
      });
      logInfo("Business owners mutation succeeded");
    };
    $[11] = bosMutation;
    $[12] = businessOwners;
    $[13] = syncAuthedUserVault;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const onBosSubmit = t5;
  const onBosListSubmit = async payload => {
    logInfo("Business owner stake update", {
      uuid: payload.uuid,
      ownershipStake: payload.ownershipStake
    });
    await bosMutation({
      body: [{
        op: "update",
        uuid: payload.uuid,
        data: {},
        ownershipStake: payload.ownershipStake
      }]
    });
    const updatedBos = await queryClient.fetchQuery(getHostedBusinessOwnersOptions({
      headers
    }));
    setBusinessOwners(updatedBos);
  };
  let t6;
  if ($[15] !== footprint.navigation) {
    t6 = () => {
      logInfo("Business owners step completed, navigating forward");
      footprint.navigation.forward();
    };
    $[15] = footprint.navigation;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const onDone = t6;
  let t7;
  if ($[17] !== hideNavigationButtons || $[18] !== onBack) {
    t7 = !hideNavigationButtons && <NavigationHeader canGoBack={true} onBack={onBack} />;
    $[17] = hideNavigationButtons;
    $[18] = onBack;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== businessOwners || $[21] !== currentRequirement || $[22] !== isPending || $[23] !== onBosListSubmit || $[24] !== onBosSubmit || $[25] !== onDone || $[26] !== token) {
    t8 = <ManageBusinessOwners authToken={token} businessOwners={businessOwners} requirement={currentRequirement} onSubmit={onBosSubmit} onBosListSubmit={onBosListSubmit} onDone={onDone} isBusy={isPending} />;
    $[20] = businessOwners;
    $[21] = currentRequirement;
    $[22] = isPending;
    $[23] = onBosListSubmit;
    $[24] = onBosSubmit;
    $[25] = onDone;
    $[26] = token;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t7 || $[29] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
function _temp3(uuid) {
  return {
    op: "delete" as const,
    uuid
  };
}
function _temp2() {
  setIdv2StoreData({
    requirementPage: "business_owners"
  });
}
function _temp(state) {
  return state.hideNavigationButtons;
}